
import axios from "axios";
import { handleSessionExpired } from "./sessionExpiry";
import { REQUEST_TIMEOUT_MS, retryOnce, normalizeApiError, isSessionError } from "./apiErrorHandling";

let loadingManager = null;

export const setLoadingManager = (manager) => {
  loadingManager = manager;
};

// ── Abort controllers ──
const activeControllers = new Map();

export const cancelRequest = (key) => {
  if (activeControllers.has(key)) {
    activeControllers.get(key).abort();
    activeControllers.delete(key);
  }
};

export const cancelAllRequests = () => {
  activeControllers.forEach((controller) => controller.abort());
  activeControllers.clear();
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
});

axiosInstance.interceptors.request.use(
  (config) => {
    if (loadingManager) loadingManager.startLoading();

    // ── Auth token ──
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // ── Build params for cancellation keys ──
    const paramsStr = config.params && !config.sensitiveRequest
      ? JSON.stringify(config.params, Object.keys(config.params).sort())
      : '';

    // ── Abort controller ──
    // Use caller-supplied requestKey or derive from method + url + params
    const controllerKey =
      config.requestKey || `${config.method}:${config.url}:${paramsStr}`;

    // Cancel previous pending call to same endpoint+params
    const dedupeable = config.method !== 'get' || config.dedupe; // opt-in for GET
    if (dedupeable && activeControllers.has(controllerKey)) {
      activeControllers.get(controllerKey).abort();
    }

    const controller = new AbortController();
    activeControllers.set(controllerKey, controller);
    config.signal = controller.signal;
    // Store key on config so response interceptor can clean up
    config._controllerKey = controllerKey;

    return config;
  },
  (error) => {
    if (loadingManager) loadingManager.stopLoading();
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (loadingManager) loadingManager.stopLoading();

    // Clean up completed controller
    if (response.config._controllerKey) {
      activeControllers.delete(response.config._controllerKey);
    }

    // ── Rolling token refresh ──
    // validate-token returns a fresh token — save it automatically
    const freshToken = response?.data?.token;
    if (freshToken && response.config.url?.includes('validate-token')) {
      localStorage.setItem('token', freshToken);
      localStorage.setItem(
        'auth_role',
        response?.data?.user?.roles?.[0] || ''
      );
    }

    return response;
  },
  async (error) => {
    if (loadingManager) loadingManager.stopLoading();

    // ── Silently ignore aborted + rate limited ──
    if (axios.isCancel(error) || error?.isRateLimited) {
      return Promise.reject(error);
    }

    // Clean up controller on error
    if (error.config?._controllerKey) {
      activeControllers.delete(error.config._controllerKey);
    }

    if (error.config?.sensitiveRequest) {
      console.error("Sensitive API request failed:", { status: error.response?.status, code: error.code });
    } else {
      console.error("API Error:", error);
    }

    // ── One automatic retry for transient GET failures ──
    const retried = await retryOnce(axiosInstance, error);
    if (retried) return retried;

    normalizeApiError(error);

    // ── 401 handler (403 = no permission, not an expired session) ──
    if (isSessionError(error)) {
      const isValidatingToken = error.config?.url?.includes('validate-token');
      const isLoggingIn       = error.config?.url?.includes('auth/login') || error.config?.url?.includes('auth/mobile-otp-');

      if (!isValidatingToken && !isLoggingIn) {
        handleSessionExpired();
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
